import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import { htmlToText } from 'core/utils/htmlToText';
import messageContentParser from './MessageContentParser';

type MarkdownNode = { type: string; value?: string; children?: MarkdownNode[] };

const SPOKEN_VALUE_TYPES = new Set(['text', 'inlineCode', 'code', 'inlineMath', 'math']);
const BLOCK_TYPES = new Set(['paragraph', 'heading', 'listItem', 'tableCell', 'code', 'math', 'blockquote', 'break']);

// Mismos delimitadores que el componente Markdown, para que `$x$` se lea igual que se pinta.
const parser = unified().use(remarkParse).use(remarkGfm).use(remarkMath, { singleDollarTextMath: true });

function collectSpokenText(node: MarkdownNode, chunks: string[]): void {
  if (node.value !== undefined && SPOKEN_VALUE_TYPES.has(node.type)) {
    chunks.push(node.value);
  }
  // Un bloque de HTML (`<p>…</p>`, `<details>`) llega entero en un solo nodo `html`, con su texto
  // dentro; una etiqueta suelta (`<br>`, `<b>`) separa palabras.
  if (node.type === 'html' && node.value !== undefined) {
    chunks.push(` ${htmlToText(node.value)} `);
  }
  node.children?.forEach((child) => collectSpokenText(child, chunks));
  if (BLOCK_TYPES.has(node.type)) {
    chunks.push(' ');
  }
}

/**
 * Texto que puede leer un lector de pantalla a partir de la respuesta del tutor: sin marcas de
 * markdown y sin los diagramas y visualizaciones, que no tienen equivalente hablado.
 */
export function toSpokenText(markdown: string | null | undefined): string {
  if (!markdown?.trim()) return '';

  const textParts: string[] = messageContentParser
    .parseContent(markdown)
    .filter((part: { type: string }) => part.type === 'text')
    .map((part: { content: string }) => part.content);

  const chunks: string[] = [];
  textParts.forEach((text) => collectSpokenText(parser.parse(text) as MarkdownNode, chunks));

  return chunks.join('').replace(/\s+/g, ' ').trim();
}
